import type { DriveItem } from "@/api/driveApi";
import { useRecent } from "@/api/hooks";
import { useMe } from "@/auth/useMe";
import { formatRelative } from "@/lib/format";
import { sameEmail, shortNameFromEmail } from "@/lib/people";
import { cn } from "@/lib/utils";
import { useDriveActions } from "./driveActions";
import { FileTile, ItemThumb } from "./items/ItemIcon";

const time = (iso: string | null | undefined) => (iso ? Date.parse(iso) || 0 : 0);

/** Why a file is suggested: someone else's newer edit, the user's last open, or the last change. */
function reasonFor(item: DriveItem, me: string | undefined): string {
    const opened = time(item.lastAccessedAt);
    if (item.modifiedBy && !sameEmail(item.modifiedBy, me) && time(item.updatedAt) > opened) {
        return `${shortNameFromEmail(item.modifiedBy)} edited · ${formatRelative(item.updatedAt)}`;
    }
    if (item.lastAccessedAt) return `You opened · ${formatRelative(item.lastAccessedAt)}`;
    return `Modified · ${formatRelative(item.updatedAt)}`;
}

/**
 * My Drive root: up to four recently used files as preview cards (the
 * Recent list, so no extra request once it's cached). Hidden when empty.
 */
export function SuggestedFiles() {
    const recent = useRecent(50);
    const actions = useDriveActions();
    const me = useMe();
    const files = (recent.data ?? []).filter((i) => i.kind === "File" && !i.isTrashed).slice(0, 4);
    if (files.length === 0) return null;
    return (
        <section aria-label="Suggested files" data-no-clear="" className="grid grid-cols-2 gap-3 px-4 pb-3.5 pt-1 md:grid-cols-3 xl:grid-cols-4">
            {files.map((f, i) => (
                <button
                    key={f.id}
                    type="button"
                    onClick={() => actions.open(f, files)}
                    title={f.name}
                    className={cn(
                        "min-w-0 overflow-hidden rounded-lg border border-[var(--drive-line)] bg-card text-left transition-shadow hover:shadow-[var(--drive-shadow-8)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        // Three columns show three cards; two and four columns show all four.
                        i === 3 && "md:hidden xl:block"
                    )}
                >
                    <span className="block border-b border-[var(--drive-line)]">
                        <ItemThumb item={f} size="suggest" />
                    </span>
                    <span className="flex min-w-0 items-center gap-2 px-2.5 pt-2">
                        <FileTile item={f} size={20} />
                        <span className="truncate text-[13px] font-semibold">{f.name}</span>
                    </span>
                    <span className="block truncate px-2.5 pb-2 pt-0.5 text-[11px] text-[var(--drive-text-3)]">{reasonFor(f, me.email)}</span>
                </button>
            ))}
        </section>
    );
}
